import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useProfile } from '../contexts/ProfileContext';
import spotlightService from '../services/SpotlightService';

/**
 * Custom hook for fetching and managing user's reactions
 * Returns channels that the user has reacted to
 * Uses the same pattern as watchlist - forces appload then searches cache
 */
export const useMyReactions = () => {
  const { isAuthenticated } = useAuth0();
  const { currentProfile } = useProfile();
  const [reactionsData, setReactionsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMyReactions = useCallback(async () => {
    if (!isAuthenticated || !currentProfile?._id) {
      setReactionsData([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('🔍 useMyReactions: Starting reaction fetch...');
      
      // Step 1: Get stored reactions from localStorage for current profile
      const storedReactions = {};
      const profilePrefix = `reaction_${currentProfile._id}_`;
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(profilePrefix)) {
          const channelId = key.replace(profilePrefix, '');
          try {
            const reaction = JSON.parse(localStorage.getItem(key));
            console.log('🔍 useMyReactions: Checking reaction for', channelId, ':', reaction);
            if (reaction && reaction !== '__REMOVED__') {
              storedReactions[channelId] = reaction;
              console.log('🔍 useMyReactions: Added reaction for', channelId, ':', reaction);
            } else {
              console.log('🔍 useMyReactions: Skipped reaction for', channelId, '(removed or invalid)');
            }
          } catch (e) {
            console.warn('Failed to parse stored reaction for', channelId, e);
          }
        }
      }
      
      console.log(`🔍 useMyReactions: Looking for reactions with prefix: ${profilePrefix}`);
      console.log(`🔍 useMyReactions: Found ${Object.keys(storedReactions).length} reactions for profile ${currentProfile.profile_name}`);

      const channelIds = Object.keys(storedReactions);
      console.log('🔍 useMyReactions: Found', channelIds.length, 'stored reactions');
      
      if (channelIds.length === 0) {
        setReactionsData([]);
        return;
      }

      // Step 2: Force appload to populate cache (like watchlist does)
      console.log('🔍 useMyReactions: Ensuring appload data is available...');
      try {
        await spotlightService.getAppLoad();
        console.log('🔍 useMyReactions: Appload completed successfully');
      } catch (appLoadError) {
        console.warn('🔍 useMyReactions: Appload failed:', appLoadError.message);
        // Continue anyway, maybe cache exists from before
      }

      // Step 3: Search the now-populated cache
      const validChannels = [];
      
      // Debug: Let's see what cache keys exist after getAppLoad
      const allCacheKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('SPOTLIGHT_') || key.startsWith('APP_LOAD') || key.startsWith('chillfree_cache_'))) {
          allCacheKeys.push(key);
        }
      }
      console.log('🔍 useMyReactions: Available cache keys after getAppLoad:', allCacheKeys);
      
      // CacheService stores APP_LOAD_DATA with prefix 'chillfree_cache_'
      const cachedAppLoadRaw = localStorage.getItem('chillfree_cache_APP_LOAD_DATA');
      let cachedAppLoad = null;
      
      if (cachedAppLoadRaw) {
        try {
          const cacheItem = JSON.parse(cachedAppLoadRaw);
          cachedAppLoad = cacheItem.data; // CacheService wraps data in {data, timestamp, ttl}
          console.log('🔍 useMyReactions: Found chillfree_cache_APP_LOAD_DATA');
        } catch (e) {
          console.warn('🔍 useMyReactions: Failed to parse cached app load data:', e);
        }
      }
      
      if (cachedAppLoad) {
        console.log('🔍 useMyReactions: Found APP_LOAD_DATA cache');
        const homepageData = cachedAppLoad?.data?.homepage || cachedAppLoad?.homepage || cachedAppLoad;
        
        if (homepageData && Array.isArray(homepageData)) {
          console.log('🔍 useMyReactions: Searching through', homepageData.length, 'categories');
          
          // Use a Set to track already added channels and prevent duplicates
          const addedChannelIds = new Set();
          
          for (const channelId of channelIds) {
            // Skip if we've already added this channel
            if (addedChannelIds.has(channelId)) {
              console.log('🔍 useMyReactions: Skipping duplicate channel:', channelId);
              continue;
            }
            
            let found = false;
            for (const category of homepageData) {
              if (category.channels) {
                // Only match against dspro_id since that's what we store reactions with
                const foundChannel = category.channels.find(channel => 
                  channel.dspro_id === channelId
                );
                if (foundChannel) {
                  validChannels.push({
                    ...foundChannel,
                    user_reaction: storedReactions[channelId],
                    reaction_timestamp: Date.now()
                  });
                  addedChannelIds.add(channelId);
                  console.log('🔍 useMyReactions: Found channel:', foundChannel.title, 'with dspro_id:', channelId);
                  found = true;
                  break;
                }
              }
            }
            if (!found) {
              console.warn('🔍 useMyReactions: Could not find channel with dspro_id:', channelId);
            }
          }
        }
      }

      // Step 4: If we still don't have channels, create minimal entries
      if (validChannels.length === 0 && channelIds.length > 0) {
        console.log('🔍 useMyReactions: No cache data found, creating minimal entries');
        for (const channelId of channelIds) {
          validChannels.push({
            _id: channelId,
            dspro_id: channelId,
            title: 'Reacted Content',
            poster: null, // ChannelCard will handle fallback
            user_reaction: storedReactions[channelId],
            reaction_timestamp: Date.now(),
            isMinimal: true
          });
        }
      }

      console.log('🔍 useMyReactions: Final result:', validChannels.length, 'channels');

      // Sort by reaction timestamp (most recent first)
      validChannels.sort((a, b) => (b.reaction_timestamp || 0) - (a.reaction_timestamp || 0));

      setReactionsData(validChannels);
    } catch (error) {
      console.error('useMyReactions: Error fetching reactions:', error);
      setError(error.message || 'Failed to load reactions');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, currentProfile?._id]);

  // Fetch reactions when component mounts or dependencies change
  useEffect(() => {
    fetchMyReactions();
  }, [fetchMyReactions]);

  // Listen for localStorage changes to update reactions in real-time
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key && currentProfile?._id && e.key.startsWith(`reaction_${currentProfile._id}_`)) {
        // Reaction was added/removed for current profile, refresh the list
        fetchMyReactions();
      }
    };

    const handleReactionChange = (e) => {
      if (e.detail && currentProfile?._id && e.detail.profileId === currentProfile._id) {
        console.log('🔄 useMyReactions: Received reactionChanged event, refreshing...', e.detail);
        // Reaction was changed for current profile, refresh the list
        fetchMyReactions();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('reactionChanged', handleReactionChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('reactionChanged', handleReactionChange);
    };
  }, [fetchMyReactions, currentProfile?._id]);

  const removeReaction = useCallback(async (channelId) => {
    if (!currentProfile?._id) return false;
    
    console.log('🗑️ useMyReactions: Starting reaction removal for channel:', channelId);
    
    try {
      // Step 1: Mark as removed in localStorage (same as useReactions does)
      // This prevents other components from showing the reaction
      const localStorageKey = `reaction_${currentProfile._id}_${channelId}`;
      console.log('🗑️ useMyReactions: Marking as removed in localStorage:', localStorageKey);
      localStorage.setItem(localStorageKey, JSON.stringify('__REMOVED__'));
      
      // Step 2: Update local state immediately (optimistic update)
      console.log('🗑️ useMyReactions: Updating local state');
      setReactionsData(prev => {
        const filtered = prev.filter(channel => 
          channel.dspro_id !== channelId && channel._id !== channelId
        );
        console.log('🗑️ useMyReactions: Filtered reactions count:', filtered.length);
        return filtered;
      });
      
      // Step 3: Dispatch custom event to notify other components
      // This ensures thumbnail hovers and PDP modals update immediately
      window.dispatchEvent(new CustomEvent('reactionChanged', {
        detail: {
          key: localStorageKey,
          channelId,
          profileId: currentProfile._id,
          reaction: null // null indicates removal
        }
      }));
      console.log('📢 useMyReactions: Dispatched reactionChanged event for removal');
      
      // Step 4: Remove from API (this might fail, but UI is already updated)
      console.log('🗑️ useMyReactions: Calling API to remove reaction');
      await spotlightService.removeUserChannelReaction(channelId);
      console.log('🗑️ useMyReactions: API removal successful');
      
      return true;
    } catch (error) {
      console.error('🗑️ useMyReactions: Error during reaction removal:', error);
      console.error('🗑️ useMyReactions: Error details:', {
        message: error.message,
        stack: error.stack,
        channelId,
        profileId: currentProfile._id
      });
      
      // Don't throw the error - the UI update already happened
      // Just log it and return false to indicate partial failure
      return false;
    }
  }, [currentProfile?._id]);

  const refreshReactions = useCallback(() => {
    fetchMyReactions();
  }, [fetchMyReactions]);

  return {
    reactionsData,
    loading,
    error,
    removeReaction,
    refreshReactions,
    hasReactions: reactionsData.length > 0
  };
};

export default useMyReactions;